import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { BLOG_POSTS, getPost } from "@/data/blogPosts";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

function Section({ section }) {
  return (
    <div className="mt-8">
      {section.h ? (
        <h2 className="font-display font-bold text-lg mb-3">{section.h}</h2>
      ) : null}
      {(section.p || []).map((para, i) => (
        <p key={i} className="text-zinc-700 leading-relaxed mb-4">{para}</p>
      ))}
      {section.list ? (
        <ul className="space-y-2 mb-4">
          {section.list.map((item, i) => (
            <li key={i} className="flex gap-3 text-zinc-700 leading-relaxed">
              <span className="text-red-600 font-bold shrink-0 mt-0.5">▸</span>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) return undefined;
    document.title = `${post.title} | Lien Scout Blog`;
    const url = `https://lienscoutapp.com/blog/${post.slug}`;
    const metaSpecs = [
      ["name", "description", post.excerpt],
      ["property", "og:type", "article"],
      ["property", "og:title", post.title],
      ["property", "og:description", post.excerpt],
      ["property", "og:image", post.image],
      ["property", "og:url", url],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", post.title],
      ["name", "twitter:description", post.excerpt],
      ["name", "twitter:image", post.image],
    ];
    const created = [];
    const restored = [];
    metaSpecs.forEach(([attr, key, value]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        restored.push([el, el.getAttribute("content")]);
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value || "");
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      author: { "@type": "Organization", name: "Lien Scout" },
      publisher: { "@type": "Organization", name: "Lien Scout" },
    });
    document.head.appendChild(ld);
    return () => {
      document.title = "Lien Scout";
      created.forEach((el) => el.remove());
      restored.forEach(([el, val]) => { if (val != null) el.setAttribute("content", val); });
      document.head.removeChild(ld);
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post-page">
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-1 font-mono-data text-[11px] uppercase tracking-widest text-red-600 hover:underline"
        >
          <ArrowLeft size={12} /> All guides
        </Link>
        <div className="mt-6 flex items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest">
          <span className="bg-yellow-300 border border-black px-2 py-0.5">{post.category}</span>
          <span className="text-zinc-500">{fmtDate(post.date)}</span>
          <span className="inline-flex items-center gap-1 text-zinc-500">
            <Clock size={12} /> {post.readTime}
          </span>
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-4 leading-tight">
          {post.title}
        </h1>
        <p className="mt-4 text-lg text-zinc-600 border-l-4 border-red-600 pl-4">{post.excerpt}</p>

        {post.image ? (
          <div
            className="mt-8 border-2 border-black shadow-[6px_6px_0_#000] overflow-hidden bg-zinc-100"
            data-testid="blog-post-hero"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[240px] sm:h-[360px] object-cover"
              loading="eager"
            />
          </div>
        ) : null}

        <div className="mt-4">
          {post.sections.map((s, i) => (
            <Section key={i} section={s} />
          ))}
        </div>

        <div className="mt-12 bg-red-600 text-white border-2 border-black p-6 shadow-[5px_5px_0_#000]">
          <div className="font-display font-extrabold text-lg">
            Put this guide to work on live county data.
          </div>
          <p className="mt-2 text-white/85 text-sm">
            Search scored tax sale listings nationwide, attach real market valuations,
            and get hot-deal alerts by email. Free 3-day trial, cancel anytime.
          </p>
          <Link
            to="/pricing"
            data-testid="blog-post-cta"
            className="mt-4 inline-flex items-center gap-2 bg-yellow-400 text-black font-display font-bold px-5 py-2.5 border-2 border-black shadow-[3px_3px_0_#000] hover:shadow-[5px_5px_0_#000] transition-shadow"
          >
            Start free trial <ArrowRight size={15} />
          </Link>
        </div>

        {others.length ? (
          <div className="mt-14">
            <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Keep reading
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white border-2 border-black p-4 shadow-[3px_3px_0_#000] hover:shadow-[5px_5px_0_#000] transition-shadow"
                >
                  <div className="font-mono-data text-[10px] uppercase text-zinc-500">{p.category}</div>
                  <div className="font-display font-bold text-sm mt-2 leading-snug group-hover:text-red-600">
                    {p.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </article>
    </MarketingShell>
  );
}
